import { useWeb3 } from "@/context/Web3Context";
import { MinusCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import { JSX, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { TfiWallet } from "react-icons/tfi";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import useMdBreakpoint from "@/hooks/useMdBreakpoint";
import { ValueSkeleton } from "../skeletons";
import magic from "@/libs/magic";
import { FramerDiv } from "../shared";
import { formatNumberLocale } from "@/utils/helpers";

type Props = {
  blockchainData: any;
  darkMode: boolean;
}

/**
 * Wallet component to display wallet information and trigger wallet connect/disconnect actions.
 *
 * This component displays the wallet's balance and allows the user to connect or disconnect the wallet
 * via a popover. It integrates with the `useWeb3` hook to handle the wallet connection state.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.blockchainData - The blockchain data associated with the wallet.
 * @param {number} props.blockchainData.balance - The current balance of the user's wallet.
 * @param {boolean} props.blockchainData.loading - Indicates if the wallet balance is loading.
 * @param {boolean} props.darkMode - A boolean flag to enable dark mode styles.
 *
 * @returns {JSX.Element} A React component that displays wallet info and actions.
 *
 * @example
 * // Usage in a parent component
 * import Wallet from './Wallet';
 *
 * function ParentComponent() {
 *   const blockchainData = { balance: 2.5, loading: false };
 *   const darkMode = true;
 *
 *   return (
 *     <Wallet
 *       blockchainData={blockchainData}
 *       darkMode={darkMode}
 *     />
 *   );
 * }
 *
 */
// TODO: Fix Magic modal z-index error when shadcn Dialog is open
function Wallet({ blockchainData, darkMode }: Props): JSX.Element {

  const [showPopover, setShowPopover] = useState(false);
  const [hasClickedConnect, setHasClickedConnect] = useState(false);
  const mdBreakpoint = useMdBreakpoint();

  const {
    connectWallet,
    disconnectWallet,
    isConnected,
    showUI,
    disabled,
    handleWalletOnPageLoad
    // isThirdPartyWallet
  } = useWeb3();

  /**
   * Prevents the wallet popover from opening when the user connects
   */
  useEffect(() => {
    if (isConnected && hasClickedConnect) {
      setShowPopover(true);
      setHasClickedConnect(false);
    } else if (isConnected && !hasClickedConnect) {
      setShowPopover(false); // Close if connected but no click
    }
  }, [isConnected, hasClickedConnect]);

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components"]);

  const backgroundColour = darkMode ? "bg-mfdarklighter text-white border-transparent shadow-gray-900": "bg-white";
  const hoverColour = darkMode ? "hover:bg-white/20": "hover:bg-gray-100";

  useEffect(() => {
    handleWalletOnPageLoad();
  }, []);

  const walletPopoverLinks: any[] = [
    {
      name: t("header.wallet.button.viewwallet"),
      icon: <UserIcon className="w-6 h-6 dark:text-white" />,
      onClick: () => {
        showUI();
      }
    },
    {
      name: t("header.wallet.button.settings"),
      icon: <UserIcon className="w-6 h-6 dark:text-white" />,
      onClick: () => {
        magic.user.showSettings();
      }
    },
    {
      name: t("header.wallet.button.disconnect"),
      icon: <MinusCircleIcon className="w-6 h-6 dark:text-white" />,
      onClick: () => {
        disconnectWallet();
        toast.warning(t("header.wallet.disconnected"), {toastId: "walletDisconnect"});
      }
    }
  ];

  return (
    <div className="flex items-center gap-x-1">
      <div className="mx-1 items-center flex text-xs md:text-lg text-gray-800 dark:text-gray-100">
        {
          blockchainData.loading ?
            <ValueSkeleton className="min-w-[100px] max-h-[20px]" />:
            formatNumberLocale(intl, blockchainData.balance, "decimal", false, false, true)
        }
      </div>
      <Popover open={showPopover} onOpenChange={setShowPopover}>
        <PopoverTrigger
          id="metaMaskContainer"
          disabled={disabled}
          // has-tooltip
          className="flex relative items-center transition-all duration-200 bg-gray-100 hover:bg-gray-200 dark:hover:bg-gray-500 ease-linear cursor-pointer
                    hover:shadow-md rounded active:scale-95 z-[99] group px-2 dark:bg-mfdarklighter"
          onClick={isConnected ? () => setShowPopover(true) : () => {setHasClickedConnect(true); connectWallet();}}
        >
          {
            isConnected ?
              <span className="text-gray-600 dark:text-white">{t("header.wallet.button.connected")}</span>
              :
              <span className="text-mforange dark:text-white">{mdBreakpoint ? t("header.wallet.button.connect") : t("common:buttons.connect")}</span>
          }
          <TfiWallet className={`ml-2 w-6 h-6 m-1 dark:text-white ${isConnected ? "text-gray-600" : "text-mforange"}`} />
        </PopoverTrigger>
        {
          isConnected && showPopover &&
          <PopoverContent
            align="end"
            className={`rounded-md flex flex-col max-w-[250px] ${backgroundColour}`}
          >
            {
              walletPopoverLinks.map(({name, icon, onClick}) => (
                <FramerDiv
                  whileHover={undefined} // this overrides the global hover effect
                  onClick={onClick}
                  key={`wallet-popover-${name}`}
                  className={`flex items-center gap-2 py-1 px-2 rounded ${hoverColour}`}
                >
                  <div className="">{icon}</div>
                  <div className="">{name}</div>
                </FramerDiv>
              ))
            }
          </PopoverContent>
        }
      </Popover>
    </div>
  );
}

export default Wallet;
